import { Fragment, type ReactNode } from 'react';

/** Small text-only renderer. Raw HTML is never interpreted, and executable
 * links never become anchors. Original content stays unchanged in storage. */
function inline(text: string): ReactNode[] {
  return text.split(/(\[[^\]]+\]\([^)]+\)|`[^`]+`|\*\*[^*]+\*\*)/g).map((part, i) => {
    const link = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(part);
    if (link && (/^https?:\/\//i.test(link[2]!) || /^\/(?!\/)/.test(link[2]!))) return <a className="underline text-accent break-words" key={i} href={link[2]} rel="noreferrer noopener">{link[1]}</a>;
    if (part.startsWith('`') && part.endsWith('`')) return <code key={i} className="break-words bg-hover px-1">{part.slice(1, -1)}</code>;
    if (part.startsWith('**') && part.endsWith('**')) return <strong key={i}>{part.slice(2, -2)}</strong>;
    return <Fragment key={i}>{part}</Fragment>;
  });
}
export function Markdown({ text, openRecord }: { text: string; openRecord?: ((path: string) => boolean) | undefined }) {
  const lines = text.replace(/\r\n/g, '\n').split('\n'), blocks: ReactNode[] = [];
  for (let i = 0; i < lines.length;) {
    const line = lines[i]!;
    if (line.startsWith('```')) {
      const code: string[] = []; i++;
      while (i < lines.length && !lines[i]!.startsWith('```')) code.push(lines[i++]!);
      i++; blocks.push(<pre key={i} className="overflow-x-auto whitespace-pre-wrap break-words border border-line p-3"><code>{code.join('\n')}</code></pre>); continue;
    }
    if (i + 1 < lines.length && line.includes('|') && /^\s*\|?\s*:?-{3,}/.test(lines[i + 1]!)) {
      const cells = (s: string) => s.trim().replace(/^\||\|$/g, '').split('|').map(v => v.trim());
      const headers = cells(line), rows: string[][] = []; i += 2;
      while (i < lines.length && lines[i]!.includes('|')) rows.push(cells(lines[i++]!));
      blocks.push(<div key={i} className="overflow-x-auto"><table className="w-full text-left"><thead><tr>{headers.map((v, j) => <th className="border border-line p-2" key={j}>{inline(v)}</th>)}</tr></thead><tbody>{rows.map((row, j) => <tr key={j}>{row.map((v, k) => <td className="border border-line p-2 align-top" key={k}>{inline(v)}</td>)}</tr>)}</tbody></table></div>); continue;
    }
    const heading = /^(#{1,6})\s+(.+)$/.exec(line);
    if (heading) { blocks.push(<p role="heading" aria-level={Math.min(6, heading[1]!.length + 1)} className="font-semibold text-body" key={i}>{inline(heading[2]!)}</p>); i++; continue; }
    if (/^\s*(?:[-*]|\d+\.)\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*(?:[-*]|\d+\.)\s+/.test(lines[i]!)) items.push(lines[i++]!.replace(/^\s*(?:[-*]|\d+\.)\s+/, ''));
      blocks.push(<ul className="list-disc pl-5 space-y-1" key={i}>{items.map((v, j) => <li key={j}>{inline(v)}</li>)}</ul>); continue;
    }
    if (!line.trim()) { i++; continue; }
    const paragraph: string[] = [line]; i++;
    while (i < lines.length && lines[i]!.trim() && !/^(?:#{1,6}\s|```|[-*]\s|\d+\.\s)/.test(lines[i]!)) paragraph.push(lines[i++]!);
    blocks.push(<p key={i} className="whitespace-pre-wrap break-words">{inline(paragraph.join('\n'))}</p>);
  }
  return <div className="space-y-3 min-w-0 leading-[1.6]" onClickCapture={event => {
    if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
    const href = (event.target as Element).closest('a[href]')?.getAttribute('href');
    if (href && openRecord?.(href)) { event.preventDefault(); event.stopPropagation(); }
  }}>{blocks}</div>;
}
